import { useContext, useEffect, useState } from "react";

import { useConfigChangesInlineActions } from "components/Actions/InlineActions/hooks/useConfigChangesInlineActions";
import Table from "components/Table";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import { DEVICE_DETAILS_CONFIGURATION_OVERVIEW_COLUMNS } from "shared/constants/columns";
import { API_IS_BEING_CANCELLED, TRUE } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import {
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    FILTER_NAMES,
    ID_TYPE,
    TABLE_NAMES
} from "shared/types/enums";

import { DetailsData } from "../../Details/types/interfaces";
import { CONFIGURATIONS } from "./constants";
import {
    getConfigurationChange,
    getFeatureFlagStatus,
    getFeatureSettings,
    getUserSentConfiguration
} from "./functions";
import {
    ConfigurationType,
    LatestConfigChanges,
    SingleDeviceData
} from "./types/interfaces";

const Overview = () => {
    const { reloadItems } = useContext(ParamsContext);
    const { detailsData } = useContext(TableContext);

    const { getData, handleResponse, cancelSource, isCanceled } = useApi();
    const { inlineActions } = useConfigChangesInlineActions(true);
    const isMounted = useIsMounted();

    const { imei }: DetailsData = detailsData;

    const [isLoading, setIsLoading] = useState(true);
    const [customData, setCustomData] = useState<ConfigurationType[]>([]);

    useEffect(() => {
        const getLatestConfigurationChanges = async () => {
            try {
                setIsLoading(true);

                const endpoints = [
                    getData(`${ENDPOINTS.Devices}/${imei}`),
                    getData(
                        `${ENDPOINTS.Devices}/${imei}${ENDPOINT_PATH_SEGMENTS.ConfigurationChanges}?${FILTER_NAMES.IsLatest}=${TRUE}`
                    )
                ];

                const [
                    { data: singleDeviceData },
                    { data: latestConfigChanges }
                ] = await Promise.all(endpoints);

                const {
                    feature_settings,
                    firmware_features
                }: SingleDeviceData = singleDeviceData;

                const { data: configChanges }: LatestConfigChanges =
                    latestConfigChanges;

                const updatedData = CONFIGURATIONS.map(configType => {
                    return {
                        imei,
                        type: configType,
                        userSentConfiguration: getUserSentConfiguration(
                            configType,
                            singleDeviceData
                        ),
                        featureSettings: getFeatureSettings(
                            configType,
                            feature_settings
                        ),
                        hasFeatureFlag: getFeatureFlagStatus(
                            configType,
                            firmware_features
                        ),
                        configurationChange: getConfigurationChange(
                            configType,
                            configChanges
                        )
                    };
                });

                setCustomData(updatedData);
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    setCustomData([]);
                    handleResponse(error);
                }
            }

            setIsLoading(false);
        };

        getLatestConfigurationChanges();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imei, reloadItems.configurationOverview]);

    return (
        <Table
            name={TABLE_NAMES.ConfigurationOverview}
            isInnerTable
            customData={customData}
            isCustomDataLoading={isLoading}
            hideSearch
            hasNoToolbar
            isNotSelectable
            initialColumns={DEVICE_DETAILS_CONFIGURATION_OVERVIEW_COLUMNS}
            inlineActions={inlineActions}
            idType={ID_TYPE.Type}
            customStyle={{
                marginTop: "16px"
            }}
        />
    );
};

export default Overview;
